import { useQuery } from "@tanstack/react-query";
import { Placeholder } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import cn from "classnames";
import {
	type FC,
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Alert, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
// import "./FormPost.css"
import { useNavigate } from "react-router-dom";
import {
	canApprove,
	canEditScheduledPost,
	DEFAULT_YOUTUBE_CATEGORY_ID,
	getBlogConstraints,
	getBlogPlatforms,
	getSocialConstraints,
	getSocialPlatforms,
	isBlogPlatform,
	isSocialPlatform,
	Platform,
	type PostDto,
	PostStatus,
	postCopyContainsUrl,
	suggestYouTubeHashtags,
	TIKTOK_PRIVACY_LEVELS,
	tiktokPostProgressLabel,
	validateTikTokMedia,
	validateYouTubeHashtagCount,
	validateYouTubeMedia,
	YOUTUBE_CATEGORIES,
	YOUTUBE_DESCRIPTION_MAX,
	YOUTUBE_PRIVACY_STATUSES,
	YOUTUBE_THUMBNAIL_HEIGHT,
	YOUTUBE_THUMBNAIL_WIDTH,
	YOUTUBE_TITLE_MAX,
	type YouTubeMediaFileInfo,
	youtubeCategoryLabel,
	youtubePrivacyLabel,
} from "shared";
import { Api } from "../api.config";
import { useBillingEntitlements } from "../billing/useSubscription";
import { useNextPost, usePostsActions } from "../calendar/usePosts";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useDialog } from "../common/dialog/Dialog";
import { MarkdownEditor } from "../common/markdown/MarkdownEditor";
import { useModal } from "../common/modal/Modal";
import { PlatformIcon } from "../common/PlatformIcon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import CharacterCounter from "../components/CharacterCounter";
import { FilesGrid } from "../components/FIlesGrid";
import { PlatformSelector } from "../components/PlatformSelector";
import DropZoneWrapper from "../content/DropZoneWrapper";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen, QueryKeys } from "../LinkGen";
import {
	createOnDrop,
	createOnDropRejected,
	MediaUploadDialog,
} from "../media/MediaUploadDialog";
import { useUploadList } from "../media/upload";
import { ModalPostPreview } from "../preview/ModalPostPreview";
import { useTikTok, useYouTube } from "../settings/useIntegrations";
import { PostVideoGenerations } from "../video/PostVideoGenerations";
import { ContentCarousel } from "./ContentCarousel";
import { useFormPostContext } from "./FormPostContext";
import { PostImageGenerator } from "./PostImageGenerator";
import { readVideoMetadataFromUrl } from "./readVideoMetadata";
import {
	isTikTokPhotoSelection,
	TikTokPostSettings,
} from "./TikTokPostSettings";

type Props = {
	editing?: boolean;
	post?: PostDto;
};

const VIDEO_EDITABLE_STATUSES: string[] = [
	PostStatus.DRAFT,
	PostStatus.SCHEDULED,
	PostStatus.FAILED,
	PostStatus.PENDING_APPROVAL,
];

export const FormPost: FC<Readonly<Props>> = ({ editing = false, post }) => {
	const [preview, setPreview] = useState<number | null>(null);
	const [rewriteInstructions, setRewriteInstructions] = useState("");
	const {
		form,
		platforms,
		siblings,
		selectedPlatforms,
		selectedFiles,
		selectedThumbnail,
		isBlog,
		needsApproval,
		isShorts,
		handlePlatformChange,
		handleAttachContent,
		handleRemoveContent,
		handleReorderFiles,
		handleSelectThumbnail,
		handleRemoveThumbnail,
		handleSaveClick,
		isSaving,
		handleDeleteClick,
		handleSubmitForApproval,
		handleApprovePost,
		handleRejectPost,
	} = useFormPostContext();

	const dialog = useDialog();
	const { addFile, addError } = useUploadList();
	/** Shorts upload UX when locked Shorts flow OR YouTube is selected on create. */
	const isYouTubeMedia =
		isShorts || selectedPlatforms.includes(Platform.YOUTUBE);
	const shortsDropzone = useDropzone({
		onDrop: async (files) => {
			dialog.open(MediaUploadDialog, { mode: "youtubeShorts" });
			await createOnDrop({ addFile, addError }, "youtubeShorts")(files);
		},
		onDropRejected: (rejections) => {
			dialog.open(MediaUploadDialog, { mode: "youtubeShorts" });
			createOnDropRejected({ addError }, "youtubeShorts")(rejections);
		},
		noClick: true,
		noKeyboard: true,
		multiple: false,
		disabled: !isYouTubeMedia,
		accept: {
			"video/mp4": [".mp4"],
			"video/quicktime": [".mov"],
		},
	});

	const tiktokMediaFeedback = useMemo(() => {
		if (
			!selectedPlatforms.includes(Platform.TIKTOK) ||
			selectedFiles.length === 0
		) {
			return null;
		}
		return validateTikTokMedia(
			selectedFiles.map((f) => ({
				extension: f.extension,
				size: f.size,
				type: f.type,
				filename: f.filename,
			})),
		);
	}, [selectedPlatforms, selectedFiles]);

	const [youtubeMediaFeedback, setYoutubeMediaFeedback] = useState<{
		errors: string[];
		warnings: string[];
	} | null>(null);

	useEffect(() => {
		if (
			!selectedPlatforms.includes(Platform.YOUTUBE) ||
			selectedFiles.length === 0
		) {
			setYoutubeMediaFeedback(null);
			return;
		}

		let cancelled = false;

		const runValidation = async () => {
			const fileInfos: YouTubeMediaFileInfo[] = selectedFiles.map((f) => ({
				extension: f.extension,
				size: f.size,
				type: f.type,
				filename: f.filename,
			}));

			for (const file of selectedFiles.filter((f) => f.type === "video")) {
				try {
					const meta = await readVideoMetadataFromUrl(file.url);
					const info = fileInfos.find((f) => f.filename === file.filename);
					if (info) {
						info.width = meta.width;
						info.height = meta.height;
						info.durationSec = meta.durationSec;
					}
				} catch {
					// validateYouTubeMedia will warn if dimensions unknown
				}
			}

			if (!cancelled) {
				const result = validateYouTubeMedia(fileInfos);
				setYoutubeMediaFeedback({
					errors: result.errors,
					warnings: result.warnings,
				});
			}
		};

		runValidation();

		return () => {
			cancelled = true;
		};
	}, [selectedPlatforms, selectedFiles]);

	const company = useCompany();
	const showYouTubePrivacy = selectedPlatforms.includes(Platform.YOUTUBE);
	const isYouTubePost = showYouTubePrivacy || isShorts;
	const { status: youtubeStatus } = useYouTube();
	const youtubeChannelsQuery = useQuery({
		queryKey: QueryKeys.Integrations.YouTube.Channels(company.id),
		queryFn: () => Api.YouTubeGetChannels().then((response) => response.data),
		enabled: showYouTubePrivacy && youtubeStatus.data.connected,
	});
	const youtubeChannels =
		youtubeChannelsQuery.data ??
		youtubeStatus.data.channels ??
		(youtubeStatus.data.profile ? [youtubeStatus.data.profile] : []);
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isXEnabled = useFeatureFlag("XIntegration");
	const { data: entitlements } = useBillingEntitlements();
	const isXEntitled = isXEnabled && entitlements.xIntegration;

	const selectablePlatforms = useMemo(() => {
		const all = isBlog ? getBlogPlatforms() : getSocialPlatforms();
		return all.filter((platform) => {
			if (platform === Platform.YOUTUBE && !isYouTubeEnabled) {
				return false;
			}
			if (platform === Platform.TIKTOK && !isTikTokEnabled) {
				return false;
			}
			if (platform === Platform.X && !isXEntitled) {
				return false;
			}
			return true;
		});
	}, [isBlog, isYouTubeEnabled, isTikTokEnabled, isXEntitled]);
	const youtubeHashtagSuggestions = useMemo(() => {
		if (!showYouTubePrivacy) {
			return [];
		}
		// Company "Describe your business" is the client's business category signal.
		const businessCategory = [
			company.description?.trim(),
			youtubeCategoryLabel(
				form.values.youtubeCategoryId ?? DEFAULT_YOUTUBE_CATEGORY_ID,
			),
		]
			.filter(Boolean)
			.join(" ");
		return suggestYouTubeHashtags(form.values.content, businessCategory);
	}, [
		showYouTubePrivacy,
		form.values.content,
		form.values.youtubeCategoryId,
		company.description,
	]);

	const emit = useAlertEmit();

	const appendYouTubeHashtag = useCallback(
		(tag: string) => {
			const trimmed = form.values.content.trimEnd();
			const next = trimmed ? `${trimmed} ${tag}` : tag;
			const hashtagCheck = validateYouTubeHashtagCount(next);
			if (!hashtagCheck.valid) {
				emit({
					type: AlertType.ERROR,
					content: hashtagCheck.errors.join(" "),
				});
				return;
			}
			form.setFieldValue("content", next);
		},
		[form, emit],
	);

	const { status: tiktokStatus } = useTikTok();
	const showTikTokPrivacy = selectedPlatforms.includes(Platform.TIKTOK);
	const tiktokPrivacyOptions = useMemo(() => {
		const fromApi = tiktokStatus.data.privacyLevelOptions;
		if (fromApi && fromApi.length > 0) {
			return fromApi;
		}
		return [...TIKTOK_PRIVACY_LEVELS];
	}, [tiktokStatus.data.privacyLevelOptions]);
	const isTikTokPhotoPost = isTikTokPhotoSelection(selectedFiles);

	const contentMaxLength = useMemo(() => {
		if (isYouTubePost) {
			return YOUTUBE_DESCRIPTION_MAX;
		}
		const socialMaxes = selectedPlatforms
			.filter(isSocialPlatform)
			.map((platform) => getSocialConstraints(platform).message.max);
		if (socialMaxes.length === 0) {
			return undefined;
		}
		return Math.min(...socialMaxes);
	}, [isYouTubePost, selectedPlatforms]);

	useEffect(() => {
		if (!showTikTokPrivacy) {
			return;
		}
		const current = form.values.tiktokPrivacyLevel;
		if (current && !tiktokPrivacyOptions.includes(current)) {
			void form.setFieldValue("tiktokPrivacyLevel", undefined);
		}
		if (
			post?.tiktokPrivacyLevel &&
			!form.values.tiktokPrivacyLevel &&
			tiktokPrivacyOptions.includes(post.tiktokPrivacyLevel)
		) {
			void form.setFieldValue("tiktokPrivacyLevel", post.tiktokPrivacyLevel);
		}
	}, [
		showTikTokPrivacy,
		tiktokPrivacyOptions,
		form.values.tiktokPrivacyLevel,
		post?.tiktokPrivacyLevel,
		form,
	]);

	const navigate = useNavigate();
	const { open } = useModal();
	const { rewrite, refine } = usePostsActions();

	const canApproveOrReject = company.myRole && canApprove(company.myRole);

	const { nextPost, refetchNextPost } = useNextPost(post?.id);

	const handleNextClick = useCallback(async () => {
		if (!post || !nextPost) {
			return;
		}

		// Auto-save before advancing, but never navigate away from a failed
		// save — the edits would be lost silently. handleSaveClick has already
		// surfaced the alert and the field errors by then.
		if (form.dirty) {
			const saved = await handleSaveClick(post.id);

			if (!saved) {
				return;
			}
		}

		// The save may have changed this post's scheduled date, which can
		// change its position in the by-date queue. Refetch rather than
		// trusting the value captured at render time, and navigate off the
		// fresh result. If the fresh result is null, the edit moved this post
		// to the end of the queue — stay put; the save already succeeded, so
		// nothing is lost.
		const freshNextPost = await refetchNextPost();

		if (!freshNextPost) {
			return;
		}

		navigate(
			isBlogPlatform(freshNextPost.platform)
				? LinkGen.BlogPost(freshNextPost.id)
				: LinkGen.Post(freshNextPost.id),
		);
	}, [post, nextPost, form.dirty, handleSaveClick, refetchNextPost, navigate]);

	const handleImageAttached = useCallback(
		(mediaId: number) => {
			const currentFiles = form.values.files ?? [];
			if (!currentFiles.includes(mediaId)) {
				form.setFieldValue("files", [...currentFiles, mediaId]);
			}
		},
		[form],
	);

	// Tiptap editor for blog posts
	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			Placeholder.configure({ placeholder: "Write your blog post..." }),
		],
		content: form.values.content || undefined,
		contentType: "markdown",
		onUpdate: ({ editor: ed }) => {
			if (isBlog) {
				const markdown = ed.getMarkdown() as string;
				form.setFieldValue("content", markdown);
			}
		},
	});

	// Sync editor content when form content changes externally (e.g., AI rewrite)
	useEffect(() => {
		if (isBlog && editor && !editor.isFocused) {
			const currentMarkdown = editor.getMarkdown() as string;
			if (currentMarkdown !== form.values.content) {
				editor.commands.setContent(form.values.content || "", {
					emitUpdate: false,
					contentType: "markdown",
				});
			}
		}
	}, [isBlog, editor, form.values.content]);

	// Get content length for character counter (plain text for markdown)
	const getContentLength = useCallback(() => {
		if (isBlog && editor) {
			return editor.getText().length;
		}
		return form.values.content.length;
	}, [isBlog, editor, form.values.content]);

	const handleRewrite = useCallback(async () => {
		if (!rewriteInstructions.trim()) return;

		let newContent: string;

		if (editing && post?.id) {
			// Existing post - use rewrite mutation
			newContent = await rewrite.mutateAsync({
				id: post.id,
				instructions: rewriteInstructions,
			});
		} else {
			// New post - use refine mutation
			const platform = selectedPlatforms[0];
			if (!platform || !form.values.content.trim()) return;

			newContent = await refine.mutateAsync({
				content: form.values.content,
				instructions: rewriteInstructions,
				platform,
				hasMedia: (form.values.files ?? []).length > 0,
			});
		}

		form.setFieldValue("content", newContent);
		setRewriteInstructions("");
	}, [
		editing,
		post?.id,
		rewriteInstructions,
		rewrite,
		refine,
		form,
		selectedPlatforms,
	]);

	const handlePreviewClick = useCallback(() => {
		open(ModalPostPreview, {
			content: form.values.content,
			files: selectedFiles,
			initialPlatform: post?.platform ?? selectedPlatforms[0],
			thumbnailFile: selectedThumbnail,
		});
	}, [
		open,
		form.values.content,
		selectedFiles,
		selectedThumbnail,
		post?.platform,
		selectedPlatforms,
	]);

	const postPlatfromFromSiblings = Object.values(Platform).map((value) => {
		const sibling = siblings?.find((s) => s.platform === value);

		const sourcePost = post?.platform === value ? post : sibling;

		if (!sourcePost) {
			return null;
		}
		return sourcePost;
	});

	const isScheduleLocked = Boolean(
		editing && post && !canEditScheduledPost(post),
	);

	return (
		<Form onSubmit={form.handleSubmit} className="single-post-form">
			{editing && post && (
				<Row>
					<Col>
						{post.status !== PostStatus.PUBLISHED && !isScheduleLocked && (
							<p>
								Select the {isBlog ? "blog platform" : "social networks"} you
								want to edit your post for.
							</p>
						)}

						<div className="d-flex gap-4 pb-3">
							{postPlatfromFromSiblings.map((value) => {
								if (!value) {
									return null;
								}
								return (
									<Fragment key={`sibling-${value.id}`}>
										<SimpleTooltip tooltip={value.platform} placement="bottom">
											<Button
												className={cn("p-2 border", {
													"border-primary": post.id === value.id,
												})}
												variant="social"
												onClick={() => {
													navigate(LinkGen.Post(value.id));
												}}
											>
												<PlatformIcon value={value.platform} />
											</Button>
										</SimpleTooltip>
									</Fragment>
								);
							})}
						</div>
					</Col>
				</Row>
			)}
			<fieldset
				disabled={isScheduleLocked}
				className="border-0 p-0 m-0 min-w-0"
			>
				{!editing && !post && !isShorts && (
					<Row>
						<Col>
							<p>
								Select the {isBlog ? "blog platform" : "social networks"} you
								want to publish your post on.
							</p>

							<PlatformSelector
								platforms={selectablePlatforms}
								values={platforms}
								onChange={handlePlatformChange}
							/>
						</Col>
					</Row>
				)}
				{editing && post && (
					<Row>
						<Col>
							<div className="d-flex align-items-center gap-2 mb-2">
								<span className={cn("input-group-text", "label", post.status)}>
									{post.status === PostStatus.IN_PROGRESS
										? (post.platform === Platform.TIKTOK
												? tiktokPostProgressLabel(
														post.publishStatus,
														post.statusDetail,
													)
												: post.statusDetail) || post.status
										: post.status}
								</span>
							</div>
							{post.status === PostStatus.FAILED && post.errorMessage && (
								<Form.Text className="text-danger d-block mt-1" role="alert">
									{post.errorMessage}
								</Form.Text>
							)}
							{post.status === PostStatus.IN_PROGRESS && post.statusDetail && (
								<Form.Text className="text-muted d-block mt-1">
									{post.platform === Platform.TIKTOK
										? tiktokPostProgressLabel(
												post.publishStatus,
												post.statusDetail,
											)
										: post.statusDetail}
								</Form.Text>
							)}
						</Col>
					</Row>
				)}
				{editing && post && (
					<Row className="mb-3">
						<Col className="d-flex justify-content-end gap-2">
							<Button
								icon="save"
								variant="secondary"
								loading={isSaving}
								disabled={isSaving}
								onClick={() => void handleSaveClick(post.id)}
							>
								Save
							</Button>
							<Button
								variant="primary"
								onClick={() => void handleNextClick()}
								disabled={!nextPost || isSaving}
								title={nextPost ? undefined : "No later post to move to"}
							>
								Next
							</Button>
						</Col>
					</Row>
				)}
				{(isBlog || isYouTubePost) && (
					<Row>
						<Col>
							<ExtraForm.Group
								label={isYouTubePost ? "Short title" : "Title"}
								required={isYouTubePost}
								id="post-subject-field"
							>
								<InputGroup className="post-input-group">
									<ExtraForm.Control
										placeholder={
											isYouTubePost
												? "Short title (required, max 100 characters)"
												: "Title"
										}
										aria-required={isYouTubePost ? true : undefined}
										aria-describedby={
											isYouTubePost
												? "post-subject-char-count youtube-media-feedback"
												: undefined
										}
										data-testid="subject"
										maxLength={isYouTubePost ? YOUTUBE_TITLE_MAX : undefined}
										{...form.getFieldProps("subject")}
										{...form.getFieldState("subject")}
									/>
								</InputGroup>
								{isYouTubePost && (
									<div className="mt-1">
										<CharacterCounter
											id="post-subject-char-count"
											current={form.values.subject?.length ?? 0}
											max={YOUTUBE_TITLE_MAX}
										/>
									</div>
								)}
							</ExtraForm.Group>
						</Col>
					</Row>
				)}
				<Row className="my-4">
					<Col lg={isBlog ? 6 : 4}>
						<ExtraForm.Group
							label={isYouTubePost ? "Description (optional)" : "Post Copy"}
							id="post-copy-field"
						>
							{isBlog && editor ? (
								<div className="blog-editor-container">
									<MarkdownEditor editor={editor} />
								</div>
							) : (
								<ExtraForm.Control
									placeholder={
										isYouTubePost
											? "Description and hashtags for your Short"
											: "Post"
									}
									as="textarea"
									rows={25}
									data-testid="post"
									aria-describedby={
										isYouTubePost
											? "post-copy-char-count youtube-media-feedback"
											: undefined
									}
									maxLength={contentMaxLength}
									{...form.getFieldProps("content")}
									{...form.getFieldState("content")}
								/>
							)}
						</ExtraForm.Group>

						{(isYouTubePost || contentMaxLength != null) && (
							<div className="mt-1">
								<CharacterCounter
									id="post-copy-char-count"
									current={form.values.content?.length ?? 0}
									max={contentMaxLength ?? YOUTUBE_DESCRIPTION_MAX}
								/>
							</div>
						)}

						{showYouTubePrivacy && youtubeHashtagSuggestions.length > 0 && (
							<fieldset className="mt-2 border-0 p-0">
								<legend className="text-muted small mb-1">
									Suggested hashtags based on your business category (click to
									add)
								</legend>
								<div className="d-flex flex-wrap gap-2">
									{youtubeHashtagSuggestions.map((tag: string) => (
										<Button
											key={tag}
											size="sm"
											variant="secondary"
											onClick={() => appendYouTubeHashtag(tag)}
											aria-label={`Add ${tag} to description`}
										>
											{tag}
										</Button>
									))}
								</div>
							</fieldset>
						)}

						{(selectedPlatforms.length > 0 || (editing && post)) && (
							<div className="mt-3 p-3 border rounded bg-light">
								<Form.Label className="fw-semibold mb-2">
									AI Edit Assistant
								</Form.Label>
								<Form.Control
									type="text"
									placeholder="e.g., Make it more casual, add emojis, shorten it..."
									value={rewriteInstructions}
									onChange={(e) => setRewriteInstructions(e.target.value)}
									disabled={rewrite.isPending || refine.isPending}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											handleRewrite();
										}
									}}
									className="mb-2"
								/>
								<Form.Text className="text-muted d-block mb-2">
									Describe how you want the AI to edit your post
								</Form.Text>
								<Button
									variant="primary"
									onClick={handleRewrite}
									loading={rewrite.isPending || refine.isPending}
									disabled={
										!rewriteInstructions.trim() ||
										(!editing && !form.values.content.trim())
									}
								>
									Rewrite
								</Button>
							</div>
						)}

						<div className="field-labels mt-3">
							<p>Platform character limitations:</p>
							{selectedPlatforms?.map((platform) => (
								<CharacterCounter
									key={`char-range-${platform}`}
									current={getContentLength()}
									max={
										isSocialPlatform(platform)
											? getSocialConstraints(platform).message.max
											: isBlogPlatform(platform)
												? getBlogConstraints(platform).content.max
												: 0
									}
									prefix={
										<SimpleTooltip
											tooltip={`${platform} maximum characters`}
											placement="top"
										>
											<PlatformIcon value={platform} />
										</SimpleTooltip>
									}
								/>
							))}
						</div>
						{selectedPlatforms.includes(Platform.X) &&
							postCopyContainsUrl(form.values.content ?? "") &&
							!entitlements.xLinkPosting && (
								<Alert variant="warning" className="mt-2">
									This X post includes a link. Activate{" "}
									<strong>X Link Posting</strong> ($25/mo) in{" "}
									<a href={LinkGen.Settings.Billing()}>Billing</a> to publish
									it, or remove the link.
								</Alert>
							)}
						{selectedPlatforms.includes(Platform.X) &&
							postCopyContainsUrl(form.values.content ?? "") &&
							entitlements.xLinkPosting && (
								<Form.Text className="text-warning d-block mt-2">
									This X post includes a link. Link posts cost more on X&apos;s
									API — prefer no link when the point stands alone.
								</Form.Text>
							)}
						{tiktokMediaFeedback && (
							<div className="mt-3">
								{tiktokMediaFeedback.errors.map((msg) => (
									<Form.Text key={msg} className="text-danger d-block">
										{msg}
									</Form.Text>
								))}
								{tiktokMediaFeedback.warnings.map((msg) => (
									<Form.Text key={msg} className="text-warning d-block">
										{msg}
									</Form.Text>
								))}
							</div>
						)}
						{youtubeMediaFeedback && (
							<div
								id="youtube-media-feedback"
								className="mt-3"
								role="alert"
								aria-live="polite"
							>
								{youtubeMediaFeedback.errors.map((msg) => (
									<Form.Text key={msg} className="text-danger d-block">
										{msg}
									</Form.Text>
								))}
								{youtubeMediaFeedback.warnings.map((msg) => (
									<Form.Text key={msg} className="text-warning d-block">
										{msg}
									</Form.Text>
								))}
							</div>
						)}
						{showYouTubePrivacy && (
							<>
								{youtubeChannels.length > 1 && (
									<ExtraForm.Group label="YouTube channel" className="mt-2">
										<ExtraForm.Select
											{...form.getFieldProps("youtubeChannelId")}
											value={
												form.values.youtubeChannelId ??
												youtubeStatus.data.profile?.id ??
												""
											}
										>
											{youtubeChannels.map((channel) => (
												<option key={channel.id} value={channel.id}>
													{channel.name}
												</option>
											))}
										</ExtraForm.Select>
										<Form.Text className="text-muted">
											Each channel was connected separately in Settings → Apps.
										</Form.Text>
									</ExtraForm.Group>
								)}
								<ExtraForm.Group label="YouTube privacy" className="mt-2">
									<ExtraForm.Select
										{...form.getFieldProps("youtubePrivacyStatus")}
										value={form.values.youtubePrivacyStatus ?? "public"}
									>
										{YOUTUBE_PRIVACY_STATUSES.map((status) => (
											<option key={status} value={status}>
												{youtubePrivacyLabel(status)}
												{status === "private"
													? " (reduces discoverability)"
													: ""}
											</option>
										))}
									</ExtraForm.Select>
								</ExtraForm.Group>
								<ExtraForm.Group label="YouTube category" className="mt-2">
									<ExtraForm.Select
										{...form.getFieldProps("youtubeCategoryId")}
										value={
											form.values.youtubeCategoryId ??
											DEFAULT_YOUTUBE_CATEGORY_ID
										}
									>
										{YOUTUBE_CATEGORIES.map((category) => (
											<option key={category.id} value={category.id}>
												{category.label}
											</option>
										))}
									</ExtraForm.Select>
								</ExtraForm.Group>
								<ExtraForm.Group label="Custom thumbnail" className="mt-2">
									<div className="d-flex align-items-center gap-2">
										<Button variant="secondary" onClick={handleSelectThumbnail}>
											{form.values.youtubeThumbnailFileId
												? "Change thumbnail"
												: "Choose thumbnail"}
										</Button>
										{form.values.youtubeThumbnailFileId && (
											<Button variant="link" onClick={handleRemoveThumbnail}>
												Remove
											</Button>
										)}
									</div>
									{selectedThumbnail && (
										<img
											src={selectedThumbnail.url}
											alt="Custom YouTube thumbnail preview"
											className="d-block mt-2 rounded border"
											style={{
												maxWidth: 240,
												aspectRatio: "16 / 9",
												objectFit: "cover",
											}}
										/>
									)}
									<Form.Text className="text-muted d-block mt-1">
										Optional JPG/PNG. Recommended {YOUTUBE_THUMBNAIL_WIDTH}×
										{YOUTUBE_THUMBNAIL_HEIGHT}. If omitted, YouTube generates
										one automatically. Unverified channels may be unable to set
										a custom thumbnail.
									</Form.Text>
								</ExtraForm.Group>
							</>
						)}
						{showTikTokPrivacy && (
							<TikTokPostSettings
								form={form}
								privacyOptions={tiktokPrivacyOptions}
								creatorName={tiktokStatus.data.profile?.name}
								commentDisabled={
									tiktokStatus.data.tiktokCommentDisabled === true
								}
								duetDisabled={tiktokStatus.data.tiktokDuetDisabled === true}
								stitchDisabled={tiktokStatus.data.tiktokStitchDisabled === true}
								isPhotoPost={isTikTokPhotoPost}
							/>
						)}
					</Col>
					<Col lg={isBlog ? 6 : 8}>
						<ExtraForm.Group
							label={isYouTubeMedia ? "Short video" : "Post images / videos"}
							id="post-media-field"
						>
							{isYouTubeMedia && (
								<Form.Text className="text-muted d-block mb-2">
									Drag & drop an MP4 or MOV here, or browse. Must be vertical
									(9:16), 60 seconds or less, and 256MB or smaller.
								</Form.Text>
							)}
							{isYouTubeMedia ? (
								<DropZoneWrapper
									dropzone={shortsDropzone}
									dropzoneText="Drop your Short video here"
								>
									<div
										className="single-post-form-preview"
										aria-describedby={
											isYouTubePost ? "youtube-media-feedback" : undefined
										}
									>
										{!preview && (
											<FilesGrid
												onClick={(id) => setPreview(id)}
												onRemove={handleRemoveContent}
												onAdd={handleAttachContent}
												onReorder={handleReorderFiles}
												files={selectedFiles}
											/>
										)}

										{!!preview && (
											<>
												<ButtonIcon
													onClick={() => setPreview(null)}
													aria-label="Close media preview"
													className="close"
													icon={"close"}
												/>
												<ContentCarousel
													index={selectedFiles.findIndex(
														(file) => file.id === preview,
													)}
													key={selectedFiles.map((f) => f.id).join(",")}
													files={selectedFiles}
												/>
											</>
										)}
									</div>
								</DropZoneWrapper>
							) : (
								<div
									className="single-post-form-preview"
									aria-describedby={
										isYouTubePost ? "youtube-media-feedback" : undefined
									}
								>
									{!preview && (
										<FilesGrid
											onClick={(id) => setPreview(id)}
											onRemove={handleRemoveContent}
											onAdd={handleAttachContent}
											onReorder={handleReorderFiles}
											files={selectedFiles}
										/>
									)}

									{!!preview && (
										<>
											<ButtonIcon
												onClick={() => setPreview(null)}
												aria-label="Close media preview"
												className="close"
												icon={"close"}
											/>
											<ContentCarousel
												index={selectedFiles.findIndex(
													(file) => file.id === preview,
												)}
												key={selectedFiles.map((f) => f.id).join(",")}
												files={selectedFiles}
											/>
										</>
									)}
								</div>
							)}
						</ExtraForm.Group>

						{!isBlog && !isYouTubeMedia && (
							<div className="mt-3 p-3 border rounded bg-light">
								<Form.Label className="fw-semibold mb-2">
									AI Image Generator
								</Form.Label>
								<Form.Text className="text-muted d-block mb-3">
									Generate an image that matches your post copy
								</Form.Text>
								<PostImageGenerator
									postContent={form.values.content}
									onImageAttached={handleImageAttached}
								/>
							</div>
						)}

						{!isBlog && editing && post && (
							<PostVideoGenerations
								postId={post.id}
								platform={post.platform}
								canEdit={VIDEO_EDITABLE_STATUSES.includes(post.status)}
								onVideoReady={handleImageAttached}
							/>
						)}

						<div className="d-flex justify-content-end gap-3" />
					</Col>
				</Row>
			</fieldset>
			<footer>
				<div className="d-flex gap-2">
					{editing && post && (
						<Button
							icon="cloud_upload"
							variant="danger"
							onClick={() => handleDeleteClick(post.id)}
						>
							{post.status === PostStatus.SCHEDULED ? "Cancel" : "Delete"}
						</Button>
					)}
					{!isBlog && (
						<Button
							icon="visibility"
							variant="primary"
							onClick={handlePreviewClick}
						>
							Preview
						</Button>
					)}
					{/* Submit for Approval button - for users who need approval */}
					{!isScheduleLocked &&
						editing &&
						post &&
						post.status === PostStatus.DRAFT &&
						needsApproval && (
							<Button
								icon="send"
								variant="primary"
								onClick={handleSubmitForApproval}
							>
								Submit for Approval
							</Button>
						)}
					{/* Approve/Reject buttons - for approvers when approval is required */}
					{!isScheduleLocked &&
						editing &&
						post &&
						canApproveOrReject &&
						(post.status === PostStatus.PENDING_APPROVAL ||
							(company.requirePostApproval &&
								post.status === PostStatus.DRAFT)) && (
							<>
								<Button
									icon="check"
									variant="primary"
									onClick={handleApprovePost}
								>
									Approve
								</Button>
								<Button
									icon="close"
									variant="danger"
									onClick={() => handleRejectPost()}
								>
									Reject
								</Button>
							</>
						)}
				</div>
			</footer>
		</Form>
	);
};
